import React, { FC, useState } from 'react';
import { CheckSquareOutlined, FileTextOutlined } from '@ant-design/icons';
import { ComplianceChecklistModal } from './ComplianceChecklistModal';
import { ComplianceRequest } from '../../types/ComplianceTypes';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';

interface RequestedComplianceCellProps {
	complianceRequest: ComplianceRequest;
	// Click handler for the compliance-type title. Omit to render it as plain text
	// (the Overview table's rows are not clickable).
	onTitleClick?: () => void;
}

// The "Requested Compliance" table cell shared by the buyer Overview and Requests
// tabs: the compliance type, then the sample document, the checklist extracted
// from it, and the request name on one footnote line.
export const RequestedComplianceCell: FC<RequestedComplianceCellProps> = ({
	complianceRequest,
	onTitleClick,
}): React.ReactElement => {
	const [checklistOpen, setChecklistOpen] = useState(false);
	const openChecklist = () => setChecklistOpen(true);
	const closeChecklist = () => setChecklistOpen(false);
	// The link has no href (it opens a modal), so it needs the keyboard activation
	// an anchor would otherwise give for free.
	const handleChecklistKeyDown = (event: React.KeyboardEvent<HTMLAnchorElement>) => {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			openChecklist();
		}
	};
	// Same for the title: it is a click target, not a link or a button element.
	const handleTitleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (onTitleClick && (event.key === 'Enter' || event.key === ' ')) {
			event.preventDefault();
			onTitleClick();
		}
	};

	const sampleFileUrl = nullSafeGet('sampleFileUrl', complianceRequest);
	// Only offer the checklist when the request actually carries one — requests
	// created before the AI extraction shipped, or whose extraction was skipped,
	// have nothing to show.
	const hasChecklist =
		nullSafeGetOrElse('checklistExtraction.items.length', complianceRequest, 0) > 0;

	return (
		<div className="flex flex-col gap-1">
			<div
				role={onTitleClick ? 'button' : undefined}
				tabIndex={onTitleClick ? 0 : undefined}
				onClick={onTitleClick}
				onKeyDown={handleTitleKeyDown}
				className={`bodymedium ${onTitleClick ? 'cursor-pointer' : ''}`}
			>
				{nullSafeGetOrElse('complianceType.name', complianceRequest, '--')}
			</div>
			<div className="footnoteregular flex flex-row flex-wrap items-center gap-1 text-character-secondary">
				<FileTextOutlined />
				{sampleFileUrl ? (
					<a href={sampleFileUrl} target="_blank" rel="noopener noreferrer">
						Sample
					</a>
				) : null}
				{sampleFileUrl ? <span>•</span> : null}
				{/* An anchor, not a Button: the global .ant-btn rule uppercases and
				    letter-spaces every antd button, which would make this read
				    "CHECKLIST" next to the plain "Sample" link. It opens a modal, so
				    there is no href to give it — role/tabIndex/onKeyDown supply the
				    keyboard behaviour the rule is guarding. */}
				{hasChecklist ? <CheckSquareOutlined /> : null}
				{hasChecklist ? (
					// eslint-disable-next-line jsx-a11y/anchor-is-valid
					<a
						role="button"
						tabIndex={0}
						className="cursor-pointer"
						onClick={openChecklist}
						onKeyDown={handleChecklistKeyDown}
					>
						Checklist
					</a>
				) : null}
				{hasChecklist ? <span>•</span> : null}
				<span>{nullSafeGetOrElse('name', complianceRequest, '--')}</span>
			</div>
			{checklistOpen ? (
				<ComplianceChecklistModal
					visible={true}
					complianceRequest={complianceRequest}
					onCancel={closeChecklist}
				/>
			) : null}
		</div>
	);
};
